import { motion } from "framer-motion";
import { TrendingDown, ExternalLink, ShieldCheck } from "lucide-react";
import type { SearchResult } from "@/pages/SearchPage";

const DirectResultCard = ({ result }: { result: SearchResult }) => {
  if (!result.foundDirect) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-card border rounded-2xl px-6 py-6"
      >
        <div className="flex items-center gap-3 text-center justify-center">
          <ShieldCheck className="w-5 h-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground font-medium">
            Verified: This host only lists on {result.platform === 'airbnb' ? 'Airbnb' : 'Vrbo'}. No cheaper direct sites found.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="bg-card border-2 border-savings/30 rounded-2xl overflow-hidden shadow-card-hover"
    >
      <div className="bg-accent px-6 py-4 flex items-center gap-3">
        <div className={`flex items-center gap-1.5 ${result.foundOwnerSite ? 'bg-savings text-savings-foreground' : 'bg-muted text-muted-foreground'} px-3 py-1.5 rounded-full text-xs font-bold`}>
          <TrendingDown className="w-3.5 h-3.5" />
          {result.foundOwnerSite ? 'Owner Site Found' : 'Possible Matches'}
        </div>
        <span className="text-sm text-accent-foreground font-medium">
          {result.foundOwnerSite
            ? 'AI verified: this is the property owner\'s direct booking site'
            : 'These sites may offer the same property — verify before booking'}
        </span>
      </div>

      <div className="px-6 py-6 space-y-4">
        {/* Primary CTA */}
        <a
          href={result.lensMatches[0].link}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-gradient-hero text-primary-foreground rounded-xl py-4 font-bold text-base flex items-center justify-center gap-2.5 hover:opacity-90 transition-opacity shadow-savings"
        >
          <ExternalLink className="w-5 h-5" />
          {result.foundOwnerSite ? 'Visit Owner\'s Direct Site' : 'View Top Match'}
        </a>


        {/* Additional matches */}
        {result.lensMatches.length > 1 && (
          <div className="pt-3 border-t">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Other matches</p>
            <div className="space-y-2">
              {result.lensMatches.slice(1).map((match, i) => (
                <a
                  key={i}
                  href={match.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-secondary rounded-lg px-4 py-3 hover:bg-secondary/80 transition-colors group"
                >
                  {match.thumbnail && (
                    <img src={match.thumbnail} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{match.title || match.source || match.link}</p>
                      {match.isOwnerSite && (
                        <span className="text-[10px] bg-savings text-savings-foreground px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">Owner</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{match.link}</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default DirectResultCard;
